import { useEffect, useRef, useState } from "react";
import { Camera, Video, RefreshCw, Check, AlertCircle } from "lucide-react";

/**
 * Records webcam video (max 30s) with the "Candidat" pizza box overlaid so the
 * user must place their head in the box cutout. Overlay is baked into the video
 * via canvas.captureStream. Calls onRecorded(blob) when done.
 */
export default function VideoRecorder({ onRecorded }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const rafRef = useRef(null);
  const overlayRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [phase, setPhase] = useState("rules"); // rules | ready | countdown | recording | done | error
  const [count, setCount] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [err, setErr] = useState("");

  const MAX = 30;

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = "/assets/box-president.png";
    overlayRef.current = img;
    return () => stopAll();
    // eslint-disable-next-line
  }, []);

  const stopAll = () => {
    cancelAnimationFrame(rafRef.current);
    clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  const drawLoop = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1); // mirror selfie
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();
    if (overlayRef.current && overlayRef.current.complete) {
      ctx.drawImage(overlayRef.current, 0, 0, canvas.width, canvas.height);
    }
    rafRef.current = requestAnimationFrame(drawLoop);
  };

  const startCamera = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 720, height: 720 }, audio: true,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      const canvas = canvasRef.current;
      canvas.width = 720; canvas.height = 720;
      drawLoop();
      setPhase("ready");
    } catch (e) {
      setErr("Impossible d'accéder à la caméra/micro. Autorisez l'accès puis réessayez.");
      setPhase("error");
    }
  };

  const beginCountdown = () => {
    setCount(3);
    setPhase("countdown");
    let c = 3;
    const iv = setInterval(() => {
      c -= 1;
      if (c === 0) { clearInterval(iv); startRecording(); }
      else setCount(c);
    }, 1000);
  };

  const startRecording = () => {
    chunksRef.current = [];
    const canvasStream = canvasRef.current.captureStream(30);
    const audio = streamRef.current.getAudioTracks();
    if (audio[0]) canvasStream.addTrack(audio[0]);
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
    const rec = new MediaRecorder(canvasStream, { mimeType: mime });
    recorderRef.current = rec;
    rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setPreviewUrl(URL.createObjectURL(blob));
      setPhase("done");
      stopAll();
      onRecorded?.(blob);
    };
    rec.start();
    setElapsed(0);
    setPhase("recording");
    let t = 0;
    timerRef.current = setInterval(() => {
      t += 1; setElapsed(t);
      if (t >= MAX) stopRecording();
    }, 1000);
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  const reset = () => {
    setPreviewUrl(null);
    onRecorded?.(null);
    startCamera();
  };

  return (
    <div className="rounded-3xl overflow-hidden bg-cc-navy text-white">
      <video ref={videoRef} className="hidden" playsInline muted />
      <div className="relative aspect-square bg-black grid place-items-center">
        {phase === "rules" && (
          <div className="p-6 text-center max-w-sm" data-testid="recorder-rules">
            <div className="rosette w-16 h-16 mx-auto mb-4 opacity-90" />
            <h3 className="font-display text-3xl">RÈGLES DU JEU</h3>
            <ul className="text-sm text-white/80 mt-4 space-y-2 text-left">
              <li className="flex gap-2"><Check size={16} className="text-cc-gold shrink-0 mt-0.5" /> Placez votre tête dans l'encart de la boîte 🇫🇷</li>
              <li className="flex gap-2"><Check size={16} className="text-cc-gold shrink-0 mt-0.5" /> 30 secondes maximum pour convaincre</li>
              <li className="flex gap-2"><Check size={16} className="text-cc-gold shrink-0 mt-0.5" /> Votre vidéo sera validée par la startup avant publication</li>
              <li className="flex gap-2"><Check size={16} className="text-cc-gold shrink-0 mt-0.5" /> Partagez ensuite avec #cocardepizza</li>
            </ul>
            <p className="font-display text-xl text-cc-gold mt-5">Vous avez 30 sec pour convaincre, soyez bon !</p>
            <button data-testid="recorder-start-camera" onClick={startCamera} className="btn-cocarde w-full mt-5 inline-flex items-center justify-center gap-2">
              <Camera size={18} /> Activer la caméra
            </button>
          </div>
        )}

        {(phase === "ready" || phase === "countdown" || phase === "recording") && (
          <canvas ref={canvasRef} className="w-full h-full object-cover" data-testid="recorder-canvas" />
        )}

        {phase === "done" && previewUrl && (
          <video src={previewUrl} controls playsInline className="w-full h-full object-cover" data-testid="recorder-preview" />
        )}

        {phase === "error" && (
          <div className="p-6 text-center" data-testid="recorder-error">
            <AlertCircle className="mx-auto text-cc-red" size={40} />
            <p className="mt-3 text-white/80 text-sm">{err}</p>
            <button onClick={startCamera} className="btn-navy mt-4 inline-flex items-center gap-2"><RefreshCw size={16} /> Réessayer</button>
          </div>
        )}

        {/* overlays */}
        {phase === "ready" && (
          <div className="absolute inset-0 grid place-items-end pb-6 bg-gradient-to-t from-black/60 to-transparent pointer-events-none">
            <p className="text-cc-gold font-display text-lg animate-pulse">Placez votre tête dans l'encart…</p>
          </div>
        )}
        {phase === "countdown" && (
          <div className="absolute inset-0 grid place-items-center bg-black/40">
            <span key={count} className="font-display text-9xl text-white fade-up" data-testid="recorder-countdown">{count}</span>
          </div>
        )}
        {phase === "recording" && (
          <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-5">
            <span className="flex items-center gap-2 bg-black/60 rounded-full px-3 py-1 text-sm font-bold"><span className="rec-dot" /> REC {elapsed}s / {MAX}s</span>
            <span className="font-display text-lg text-cc-gold">C'est à vous !</span>
          </div>
        )}
        {phase === "recording" && (
          <div className="absolute bottom-0 left-0 h-1.5 bg-cc-red transition-all" style={{ width: `${(elapsed / MAX) * 100}%` }} />
        )}
      </div>

      <div className="p-4 bg-cc-navy">
        {phase === "ready" && (
          <button data-testid="recorder-rec-btn" onClick={beginCountdown} className="btn-cocarde w-full inline-flex items-center justify-center gap-2">
            <Video size={18} /> 3, 2, 1… Rec !
          </button>
        )}
        {phase === "recording" && (
          <button data-testid="recorder-stop-btn" onClick={stopRecording} className="btn-navy w-full">Arrêter l'enregistrement</button>
        )}
        {phase === "done" && (
          <button data-testid="recorder-retake-btn" onClick={reset} className="w-full text-white/80 font-semibold inline-flex items-center justify-center gap-2">
            <RefreshCw size={16} /> Refaire la vidéo
          </button>
        )}
      </div>
    </div>
  );
}
